// budget-map.component.ts
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { NestedTreeControl } from "@angular/cdk/tree";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { FinancialService } from "src/app/shared/services/financial.service";
import { MatTableDataSource } from "@angular/material/table";
import textConfiguration from "src/assets/static-text-configuration.json";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { getCurrencySymbol } from "src/app/shared/pipes/app-format.pipe";
import { CommonService } from "src/app/shared/services/common.service";
interface DialogData {
  projectId?: string;
  portfolioId?: string;
  subportfolioId?: string;
  programId?: string;
  projectName?: string;
  portfolioName?: string;
  subportfolioName?: string;
  programName?: string;
  tagIds?: string[];
  vendorName?: string;
  vendorId?: string;
  isBreadcrumb: boolean;
}

interface TreeNode {
  name: string;
  type: "initiative" | "tag" | "project";
  data?: any;
  children?: TreeNode[];
  expanded?: boolean;
}

interface FlatNode {
  name: string;
  type: "initiative" | "tag" | "project";
  data: any;
  level: number;
  expandable: boolean;
  expanded?: boolean;
  parent?: FlatNode | null;
}

interface ApplicationData {
  tags: Array<{ id: number; tag_name: string }>;
  applications: Array<{ id: number; name: string }>;
  projects: Array<{
    id: number;
    project_name: string;
    project_id: number;
    tags: number[];
    financials: {
      total_budget: number;
      total_consumed: number;
      projected_benefit: number;
      status: string;
      roi: number;
    };
    applications: number[];
  }>;
}

interface ApplicationTableRow {
  project_name: string;
  project_id: number;
  total_budget: number;
  total_consumed: number;
  applications: { [key: number]: boolean };
  tags: number[];
  budget_status: string;
}

@Component({
  selector: "app-budget-map",
  templateUrl: "./budget-map.component.html",
  styleUrls: ["./budget-map.component.scss"],
})
export class BudgetMapComponent implements OnInit, OnDestroy {
  selectedTab = 0;
  loading = false;
  initiativeData: any[] = [];
  displayLabels: any = {};
  breadCrumbData: string[] = [];
  staticText = textConfiguration as any;
  flatNodes: FlatNode[] = [];
  visibleNodes: FlatNode[] = [];
  displayedColumnsTree = ["Name", "Budget", "Spend"];
  private destroy$ = new Subject<void>();

  // Initiative View
  treeControl = new NestedTreeControl<TreeNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();

  // Application View
  applicationData?: ApplicationData | null = null;
  applicationTableData = new MatTableDataSource<ApplicationTableRow>();
  displayedColumns?: string[] = [];
  allApplications: Array<{ id: number; name: string }> = [];
  applicationSearchTerm: string = "";
  filteredApplications: Array<{ id: number; name: string }> = [];

  // Tag colors mapping
  tagColors: { [key: number]: string } = {};
  private colorPalette = [
    "#E3F2FD", // Light Blue
    "#F3E5F5", // Light Purple
    "#E8F5E9", // Light Green
    "#FFF3E0", // Light Orange
    "#FCE4EC", // Light Pink
    "#F1F8E9", // Light Lime
    "#E0F2F1", // Light Teal
    "#FFF9C4", // Light Yellow
    "#FFEBEE", // Light Red
    "#F5F5F5", // Light Gray
  ];

  nodeColors = {
    initiative: "#1976d2",
    tag: "#f57c00",
    project: "#424242",
  };

  getCurrencySymbol(): string {
    return getCurrencySymbol();
  }
  shouldShowSymbol(value: any): boolean {
    // Convert value to string to test numeric content
    const str = String(value).trim();

    // Check if string represents a valid integer/float number
    const isNumeric = !isNaN(Number(str)) && str !== "";

    // If it's numeric (number or numeric string), do NOT show symbol
    return !isNumeric;
  }
  constructor(
    public dialogRef: MatDialogRef<BudgetMapComponent>,
    private readonly financeService: FinancialService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.displayLabels = this.commonService.getLabels();
    this.breadCrumbData.push(this.displayLabels?.all_of_it);
    if (this.data.projectName) {
      this.breadCrumbData.push(this.data.portfolioName ?? "Domain");
      this.breadCrumbData.push(this.data.subportfolioName ?? "Subdomain");
      this.breadCrumbData.push(this.data.programName ?? "Program");
      this.breadCrumbData.push(this.data.projectName);
    } else if (this.data.programName) {
      this.breadCrumbData.push(
        this.data.portfolioName ?? "Domain",
        this.data.subportfolioName ?? "Subdomain",
        this.data.programName
      );
    } else if (this.data.subportfolioName) {
      this.breadCrumbData.push(
        this.data.portfolioName ?? "Domain",
        this.data.subportfolioName
      );
    } else if (this.data.portfolioName) {
      this.breadCrumbData.push(this.data.portfolioName);
    }
    if (this.data.vendorName) {
      this.breadCrumbData.push("OU/GDP", this.data.vendorName);
    }
    this.loadInitiativeData();
  }

  hasChild = (_: number, node: TreeNode) =>
    !!node.children && node.children.length > 0;

  getPayload() {
    const params: string[] = [];

    const multiKeys = [
      { key: "project_id", value: this.data.projectId },
      { key: "program_id", value: this.data.programId },
      { key: "subportfolio_id", value: this.data.subportfolioId },
      { key: "portfolio_id", value: this.data.portfolioId },
      { key: "vendor_id", value: this.data.vendorId },
    ];

    multiKeys.forEach((item) => {
      if (!item.value) return;

      // Accept both: single value or array of values
      const joinedValue = Array.isArray(item.value)
        ? item.value.join(",")
        : item.value;

      params.push(`${item.key}=${joinedValue}`);
    });

    return params.length ? params.join("&") : "";
  }

  loadInitiativeData(): void {
    this.loading = true;
    const queryParam = this.getPayload();

    this.financeService
      .getBudgetSetupInitiatives(queryParam)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.initiativeData = res;
        // this.buildTreeData();
        this.buildTreeTableData();
        this.loading = false;
      });
  }

  loadApplicationViewData(): void {
    this.loading = true;

    // Simulate API call with setTimeout
    this.financeService
      .getBudgetSetupApplicatinoView(this.getPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.applicationData = res;
        this.allApplications = [...res.applications];
        this.filteredApplications = [...this.allApplications];
        this.assignTagColors();
        this.buildApplicationTable();
        this.loading = false;
      });
  }

  assignTagColors(): void {
    if (!this.applicationData) return;

    // Sort tags by tag.id before assigning colors
    const sortedTags = [...this.applicationData.tags].sort(
      (a, b) => a.id - b.id
    );

    sortedTags.forEach((tag, index) => {
      this.tagColors[tag.id] =
        this.colorPalette[index % this.colorPalette.length];
    });
    this.applicationData.tags = sortedTags;
  }

  getRowBackgroundColor(row: ApplicationTableRow): string {
    if (row.tags && row.tags.length > 0) {
      const firstTag = row.tags[0];
      return this.tagColors[firstTag] || "white";
    }
    return "white";
  }

  getTagName(tagId: number): string {
    if (!this.applicationData) return "";
    const tag = this.applicationData.tags.find((t) => t.id === tagId);
    return tag ? tag.tag_name : "";
  }

  buildApplicationTable(): void {
    if (!this.applicationData) return;

    // Build dynamic columns: project_name, budget_spend, then each application
    this.displayedColumns = [
      "project_name",
      "budget_spend",
      ...this.filteredApplications.map((app) => `app_${app.id}`),
    ];

    // Build table data
    const tableData: ApplicationTableRow[] = this.applicationData.projects.map(
      (project) => {
        const applications: { [key: number]: boolean } = {};

        // Check which applications this project has
        this.allApplications?.forEach((app) => {
          applications[app.id] = project.applications.includes(app.id);
        });

        return {
          project_name: project.project_name,
          budget_status: project.financials.status,
          tags: project.tags,
          project_id: project.project_id,
          total_budget: project.financials.total_budget,
          total_consumed: project.financials.total_consumed,
          applications: applications,
        };
      }
    );
    this.applicationTableData.data = tableData;
  }

  onApplicationSearch(): void {
    const searchLower = this.applicationSearchTerm.toLowerCase().trim();

    if (!searchLower) {
      this.filteredApplications = [...this.allApplications];
    } else {
      this.filteredApplications = this.allApplications.filter((app) =>
        app.name.toLowerCase().includes(searchLower)
      );
    }

    this.buildApplicationTable();
  }

  getApplicationName(appId: number): string {
    const app = this.allApplications.find((a) => a.id === appId);
    return app ? app.name : "";
  }

  buildTreeData(): void {
    const treeData: TreeNode[] = this.initiativeData
      ?.filter((initiative) => {
        const hasValidTags =
          initiative.has_subcategory &&
          initiative.tags?.some(
            (t: any) => t.projects && t.projects.length > 0
          );

        const hasProjects =
          !initiative.has_subcategory &&
          initiative.projects &&
          initiative.projects.length > 0;

        return hasValidTags || hasProjects;
      })
      .map((initiative) => ({
        name: initiative.name,
        type: "initiative" as const,
        data: initiative,
        children: initiative.has_subcategory
          ? initiative.tags
              ?.filter((tag: any) => tag.projects && tag.projects.length > 0)
              ?.map((tag: any) => ({
                name: tag.tag_name,
                type: "tag" as const,
                data: tag,
                children: tag.projects.map((project: any) => ({
                  name: project.project_name,
                  type: "project" as const,
                  data: project,
                  children: [],
                })),
              }))
          : initiative.projects.map((project: any) => ({
              name: project.project_name,
              type: "project" as const,
              data: project,
              children: [],
            })),
      }));

    this.dataSource.data = treeData;
  }

  getFormattedValue(val: number): string {
    if (!val) return "0";

    const absVal = Math.abs(val);
    if (absVal >= 1e9) {
      return (val / 1e9).toFixed(1) + "B";
    } else if (absVal >= 1e6) {
      return (val / 1e6).toFixed(1) + "M";
    } else if (absVal >= 1e3) {
      return (val / 1e3).toFixed(1) + "K";
    } else {
      const formattedVal = absVal.toFixed(2);
      return formattedVal.toString();
    }
  }
  // --- NEW --- Flatten nested tree into FlatNode[]
  flattenTree(
    nodes: any[],
    level = 0,
    parent: FlatNode | null = null
  ): FlatNode[] {
    const result: FlatNode[] = [];

    const traverse = (node: any, lvl: number, parentNode: FlatNode | null) => {
      // If the parent is a tag and the current node is a project, increase level by 1
      let computedLevel = lvl;
      if (node.type === "project") {
        if (parentNode?.type === "initiative") {
          computedLevel = lvl + 3; // project under initiative
        } else if (parentNode?.type === "tag") {
          computedLevel = lvl + 2; // project under tag
        }
      }

      const flatNode: FlatNode = {
        name: node.name,
        type: node.type,
        data: { ...node.data, children: node.children || [] },
        level: computedLevel,
        expandable: node.expandable ?? node.children?.length > 0,
        expanded: node.expanded ?? false,
        parent: parentNode,
      };

      result.push(flatNode);

      node.children?.forEach(
        (child: any) => traverse(child, computedLevel + 1, flatNode) // increment level for children
      );
    };

    nodes.forEach((node) => traverse(node, level, parent));

    return result;
  }

  //Build flat tree table
  buildTreeTableData() {
    const treeData = this.initiativeData
      ?.filter((initiative) => {
        const hasValidTags =
          initiative.has_subcategory &&
          initiative.tags?.some((t: any) => t.projects?.length > 0);
        const hasProjects =
          !initiative.has_subcategory && initiative.projects?.length > 0;
        return hasValidTags || hasProjects;
      })
      .map((initiative) => this.mapInitiativeToNode(initiative));

    this.flatNodes = this.flattenTree(treeData);
    this.updateVisibleNodes();
  }

  // Recursive mapping functions
  mapInitiativeToNode(initiative: any): any {
    const children = initiative.has_subcategory
      ? initiative.tags
          ?.filter((tag: any) => tag.projects?.length > 0)
          ?.map((tag: any) => this.mapTagToNode(tag)) || []
      : initiative.projects?.map((project: any) =>
          this.mapProjectToNode(project)
        ) || [];

    return {
      name: initiative.name,
      type: "initiative",
      data: { ...initiative, children },
      children,
    };
  }

  mapTagToNode(tag: any): any {
    const children =
      tag.projects?.map((project: any) => this.mapProjectToNode(project)) || [];
    return {
      name: tag.tag_name,
      type: "tag",
      data: { ...tag, children },
      children,
      expandable: children.length > 0, // explicitly set expandable
    };
  }

  mapProjectToNode(project: any): any {
    return {
      name: project.project_name,
      type: "project",
      data: { ...project, children: [] },
      children: [],
    };
  }

  updateVisibleNodes() {
    const result: FlatNode[] = [];

    const addNode = (node: FlatNode) => {
      // Only show node if parent is expanded or node is top-level
      if (node.parent && !node.parent.expanded) return;

      result.push(node);

      if (node.expandable && node.expanded) {
        // Add children recursively from flatNodes
        this.flatNodes
          .filter((n) => n.parent === node)
          .forEach((childNode) => addNode(childNode));
      }
    };

    // Start with top-level nodes (no parent)
    this.flatNodes.filter((n) => !n.parent).forEach((node) => addNode(node));

    this.visibleNodes = result;
  }

  toggleNode(node: FlatNode) {
    node.expanded = !node.expanded;
    this.updateVisibleNodes();
  }

  countProjects(node: FlatNode): number {
    if (node.type === "project") return 1;

    if (!node.data?.children || node.data.children.length === 0) return 0;

    // Recursively count project children
    return node.data.children.reduce((acc: number, child: any) => {
      return acc + this.countProjects(child);
    }, 0);
  }

  getNodeIcon(node: TreeNode): string {
    switch (node.type) {
      case "initiative":
        return "folder";
      case "tag":
        return "label";
      case "project":
        return "work";
      default:
        return "fiber_manual_record";
    }
  }

  getNodeClass(node: FlatNode | TreeNode): string {
    switch (node.type) {
      case "initiative":
        return "tree-node-initiative"; // blue
      case "tag":
        return "tree-node-tag"; // orange
      case "project":
        return "tree-node-project"; // grey
      default:
        return "";
    }
  }

  onTabChange(index: number): void {
    this.selectedTab = index;
    if (index === 1 && !this.applicationData) {
      this.loadApplicationViewData();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
